import { useCallback, useEffect, useRef, useState } from 'react'

import Tooltip, { TOOLTIP_TONE, WIDE } from '@aytm/app/stats/components/tooltip/Tooltip'
import type { TooltipTone } from '@aytm/app/stats/components/tooltip/Tooltip'

// The report and its sidebar. The platform's own header and footer sit outside it and keep their own
// behaviour -- this is the page's tooltip, not the shell's.
const ROOT = '.-body__content'

// Surfaces that draw their own tips. A chart's tooltip carries values and follows the cursor, which
// is a different object from a label on a control.
const FOREIGN = 'iframe, svg, [data-no-tooltip]'

// Long enough to cross a header of marks without setting off a string of pills, short enough that a
// reader who paused on one is not left waiting for it.
const OPEN_MS = 340
// Having paid that wait once, moving along a row of neighbouring marks answers at once.
const WARM_MS = 700
// Past this the copy is a sentence rather than a label, and takes a measure to read on. Copy that
// arrives with its own line breaks takes one whatever its length, since a single line cannot hold it.
const WRAP_OVER = 28
const wraps = (text: string) => text.length > WRAP_OVER || text.includes('\n')
// A tooltip is not always declared on the mark it describes: the report has strips that carry one for
// a row of icons, and one of those measures 828 wide and nothing at all tall, its icons overflowing
// it. Pointing at a box like that points nowhere near what the reader is on, so the element under the
// pointer is taken as the anchor instead and only the words come from the strip. The width is the
// pill's own WIDE, so the two agree on what counts as a strip.
const FLAT = 6

type Claim = {
  anchor: HTMLElement
  text: string
  pointer: { x: number; y: number } | null
  tone: TooltipTone
}

// Only a strip's pill follows the pointer along it, so only there does a moved pointer make a new
// claim; on an ordinary mark the pill stands where the mark is however the pointer wanders over it.
const sameColumn = (held: Claim, next: Claim) =>
  !held.pointer || !next.pointer || held.anchor.getBoundingClientRect().width <= WIDE
    ? true
    : held.pointer.x === next.pointer.x

// The names this host wrote, so a state change can keep them current without ever overwriting one an
// author set.
const named = new WeakSet<Element>()

// ARIA lets a name sit only on an element with a role of its own or on a natively named one; on a
// plain span or div it is a prohibited attribute, so there the words stay in the tooltip alone.
const NAMEABLE =
  'a[href], button, input, select, textarea, img, [role]:not([role="presentation"]):not([role="none"]):not([role="generic"])'

// A declared tooltip is moved off `title` for good rather than borrowed for the length of a hover.
// Borrowing looked tidier and does not survive the page: a component that re-renders while the
// pointer is on it writes its `title` prop back, and the browser draws its own tooltip over ours --
// which is exactly what the chart's zoom control did, since the chart re-renders under the pointer.
//
// The attribute is emptied rather than removed. An empty one draws no tooltip of its own, and keeping
// it there is what lets a control take its tooltip BACK: React clearing a `title` it believes it set
// raises no mutation at all if the attribute is already gone, so a removed one would leave the words
// standing on a control that had stopped offering them -- a question head still promising to collapse
// after the question was opened for editing.
//
// An empty `title` therefore means two things, told apart by what it replaced: ours replaces the words,
// while a control saying it has no tooltip now writes its empty value over the one we already left.
function adopt(el: Element, previous?: string | null) {
  if (el.closest(FOREIGN)) return

  const own = el.getAttribute('title')
  // the control has emptied its tooltip: nothing to say is no tooltip at all
  if ('' === own && '' === previous) return forget(el)
  // our own emptying, seen a second time through the observer
  if ('' === own) return
  // the control has taken its tooltip back
  if (null === own) return forget(el)

  el.setAttribute('title', '')
  const text = own.trim()
  if (!text) return forget(el)

  ;(el as HTMLElement).dataset.tooltip = text
  // Only where the words are the element's only name: an author's own label is never overwritten, and
  // an element that already reads its name out of its own text keeps that as its name.
  if (
    named.has(el) ||
    (el.matches(NAMEABLE) &&
      !el.getAttribute('aria-label') &&
      !el.getAttribute('aria-labelledby') &&
      !el.textContent?.trim())
  ) {
    el.setAttribute('aria-label', text)
    named.add(el)
  }
}

function forget(el: Element) {
  delete (el as HTMLElement).dataset.tooltip
  if (named.has(el)) {
    el.removeAttribute('aria-label')
    named.delete(el)
  }
}

function adoptWithin(node: Node) {
  if (!(node instanceof Element) || node.closest(FOREIGN)) return

  if (node.hasAttribute('title')) adopt(node)
  if (node.firstElementChild) node.querySelectorAll('[title]').forEach(el => adopt(el))
}

function claimFrom(node: EventTarget | null, pointer: { x: number; y: number } | null): Claim | null {
  if (!(node instanceof Element)) return null

  const el = node.closest<HTMLElement>('[data-tooltip]')
  if (!el || !el.closest(ROOT) || el.closest(FOREIGN)) return null

  const text = (el.dataset.tooltip || '').trim()
  if (!text) return null

  // the words come from whatever declared them; what the pill points at is the mark under the pointer
  const box = el.getBoundingClientRect()
  const under = node instanceof HTMLElement && node !== el ? node : null
  const anchor = under && (box.width > WIDE || box.height < FLAT) ? under : el

  // A pale pill reads on the report's own white; over a surface that is already tinted and closely
  // stacked it becomes one more pale shape among many. The region says which tone it needs rather
  // than each mark in it, so a busy surface opts in once.
  const region = el.closest<HTMLElement>('[data-tooltip-tone]')
  const tone: TooltipTone = 'deep' == region?.dataset.tooltipTone ? 'deep' : TOOLTIP_TONE

  return { anchor, text, pointer, tone }
}

// One of these on the page, listening for the whole report. Every mark that already declares a
// tooltip is answered by it, including any added later and any drawn by the older chart views, which
// is what keeps this to three files instead of one per call site.
export default function TooltipHost() {
  const [shown, setShown] = useState<Claim | null>(null)
  const timer = useRef<number | null>(null)
  const warmUntil = useRef(0)
  const pending = useRef<Claim | null>(null)
  // read inside callbacks that must not be rebuilt every time a pill opens
  const shownRef = useRef(false)
  shownRef.current = Boolean(shown)

  const hide = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = null
    pending.current = null
    if (shownRef.current) warmUntil.current = Date.now() + WARM_MS
    setShown(null)
  }, [])

  const open = useCallback(
    (next: Claim, immediate: boolean) => {
      const held = pending.current
      // Same mark, same words, same column of a strip: nothing to do. Comparing the mark alone was not
      // enough -- a control whose words change under a still pointer (the refresh button, which reads
      // "Refreshing data" while it works) kept the sentence it opened with, and a pointer travelling
      // along a strip left the pill behind at the column it entered.
      if (held && held.anchor === next.anchor && held.text === next.text && sameColumn(held, next)) return

      pending.current = next

      // Already up: exchange the words in place rather than making the reader wait for them again.
      if (shownRef.current) return setShown(next)

      if (timer.current) window.clearTimeout(timer.current)
      const wait = immediate || Date.now() < warmUntil.current ? 0 : OPEN_MS
      timer.current = window.setTimeout(() => {
        timer.current = null
        const claim = pending.current
        // gone from the page while we waited -- a row redrawn under a still pointer
        if (!claim || !claim.anchor.isConnected) return hide()
        setShown(claim)
      }, wait)
    },
    [hide]
  )

  // The report is redrawn constantly -- blocks re-render, the older views replace whole subtrees -- so
  // adopting once at mount would cover only what happened to exist then.
  useEffect(() => {
    const root = document.querySelector<HTMLElement>(ROOT)
    if (!root) return

    adoptWithin(root)

    const watcher = new MutationObserver(records => {
      for (const record of records) {
        if ('attributes' === record.type) adopt(record.target as Element, record.oldValue)
        else record.addedNodes.forEach(adoptWithin)
      }
    })
    watcher.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['title']
    })

    return () => watcher.disconnect()
  }, [])

  useEffect(() => {
    const root = document.querySelector<HTMLElement>(ROOT)
    if (!root) return

    const onOver = (event: MouseEvent) => {
      const next = claimFrom(event.target, { x: event.clientX, y: event.clientY })
      if (next) open(next, false)
      else if (pending.current) hide()
    }
    // Focus reaches these marks by keyboard, where there is no pointer to pause: the wait would only
    // read as the page being slow.
    const onFocus = (event: FocusEvent) => {
      const next = claimFrom(event.target, null)
      if (next) open(next, true)
    }

    // Listened for on the document rather than on the report: a pointer that leaves for the page
    // header raises nothing inside the report, so a pill given up on the way out would stay up.
    document.addEventListener('mouseover', onOver)
    document.addEventListener('mouseleave', hide)
    root.addEventListener('focusin', onFocus)
    root.addEventListener('focusout', hide)
    // The pill is fixed to the window, so anything that moves the page under it leaves it pointing at
    // whatever has taken its target's place. Dragging a question moves a great deal at once.
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    document.addEventListener('pointerdown', hide, true)
    document.addEventListener('dragstart', hide, true)
    document.addEventListener('keydown', hide, true)

    return () => {
      document.removeEventListener('mouseover', onOver)
      document.removeEventListener('mouseleave', hide)
      root.removeEventListener('focusin', onFocus)
      root.removeEventListener('focusout', hide)
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
      document.removeEventListener('pointerdown', hide, true)
      document.removeEventListener('dragstart', hide, true)
      document.removeEventListener('keydown', hide, true)
    }
  }, [hide, open])

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current)
    },
    []
  )

  if (!shown) return null

  return (
    <Tooltip
      text={shown.text}
      target={shown.anchor}
      pointer={shown.pointer}
      wrap={wraps(shown.text)}
      tone={shown.tone}
    />
  )
}
